import { NFT, WalletAction } from "@/app/types";

export const NFT_TYPES = {
	admin: [
		"Coupons/Vouchers",
		"Work Orders",
		"Original Art",
		"Event",
		"Tourism Collectibles",
		"MGT Drops",
		"Hidden",
		"Brand Piece",
		"Geocaching",
		"Other",
	],
	user: [
		"Original Art",
		"Photos",
		"Event Exclusive",
		"Collectibles",
		"Coupons/Vouchers",
		"Brand Piece",
		"Geocaching/Games",
		"Raffle Tickets",
		"MGT Drops",
		"Hidden",
		"Other",
	],
	all: [
		"Work Orders",
		"Original Art",
		"Event",
		"Tourism Collectibles",
		"MGT Drops",
		"Hidden",
		"Geocaching",
		"Geocaching/Games",
		"Photos",
		"Brand Piece",
		"Event Exclusive",
		"Collectibles",
		"Coupons/Vouchers",
		"Raffle Tickets",
		"Other",
	],
};

export const DEFAULT_CENTER: [number, number] = [-50.0, 40.0]; // Center on North Atlantic to show Europe and North America

export const INITIAL_ZOOM = 2.5; // Better zoom level to show both continents without cutting off markers
export const CLUSTER_RADIUS = 80;
export const CLUSTER_MAX_ZOOM = 14;
export const MAX_CLUSTER_COUNT = 100; // Cap cluster counts to prevent memory issues
export const PIN_SIZE = 0.3; // Scales 82x132 image to ~8x13 px on screen
export const INDIVIDUAL_PIN_SIZE = 0.3; // Size for individual .png pins (~60-70px from 4500px source)

// Responsive pin sizes for different screen sizes
export const PIN_SIZES = {
	desktop: 0.3, // Original size for desktop
	tablet: 0.25, // Slightly smaller for tablets
	mobile: 0.2, // Smaller for mobile devices
	mobileSmall: 0.15, // Even smaller for small mobile screens
};

// Function to get responsive pin size based on screen width
export const getResponsivePinSize = (): number => {
	if (typeof window === "undefined") return PIN_SIZES.desktop; // SSR fallback

	const width = window.innerWidth;

	if (width <= 480) return PIN_SIZES.mobileSmall;
	if (width <= 768) return PIN_SIZES.mobile;
	if (width <= 1024) return PIN_SIZES.tablet;
	return PIN_SIZES.desktop;
};

export const INITIAL_TIME = 15 * 60;

// Phantom configuration - updated for new SDK
export const phantomConfig = {
	chainPlugins: [], // Will be configured with Solana plugin in component
};

export const COUPONS_VOUCHERS_TYPE = "Coupons/Vouchers";

export const getNFTTypeColor = (type: string) => {
	const types: {
		[key: string]: { bg: string; text: string; border: string };
	} = {
		"Work Orders": {
			bg: "bg-blue-400/10",
			text: "text-blue-300",
			border: "border-blue-300",
		},
		"Original Art": {
			bg: "bg-green-400/10",
			text: "text-green-300",
			border: "border-green-300",
		},
		Event: {
			bg: "bg-yellow-400/10",
			text: "text-yellow-300",
			border: "border-yellow-300",
		},
		"Tourism Collectibles": {
			bg: "bg-teal-400/10",
			text: "text-teal-300",
			border: "border-teal-300",
		},
		"MGT Drops": {
			bg: "bg-green-400/10",
			text: "text-green-300",
			border: "border-green-300",
		},
		Hidden: {
			bg: "bg-gray-400/10",
			text: "text-gray-300",
			border: "border-gray-300",
		},
		Geocaching: {
			bg: "bg-emerald-400/10",
			text: "text-emerald-300",
			border: "border-emerald-300",
		},
		"Geocaching/Games": {
			bg: "bg-emerald-400/10",
			text: "text-emerald-300",
			border: "border-emerald-300",
		},
		Photos: {
			bg: "bg-rose-400/10",
			text: "text-rose-300",
			border: "border-rose-300",
		},
		"Brand Piece": {
			bg: "bg-indigo-400/10",
			text: "text-indigo-300",
			border: "border-indigo-300",
		},
		"Event Exclusive": {
			bg: "bg-amber-400/10",
			text: "text-amber-300",
			border: "border-amber-300",
		},
		Collectibles: {
			bg: "bg-cyan-400/10",
			text: "text-cyan-300",
			border: "border-cyan-300",
		},
		"Coupons/Vouchers": {
			bg: "bg-orange-400/10",
			text: "text-orange-300",
			border: "border-orange-300",
		},
		"Raffle Tickets": {
			bg: "bg-pink-400/10",
			text: "text-pink-300",
			border: "border-pink-300",
		},
		Other: {
			bg: "bg-slate-400/10",
			text: "text-slate-300",
			border: "border-slate-300",
		},
		default: {
			bg: "bg-gray-400/10",
			text: "text-gray-300",
			border: "border-gray-300",
		},
	};
	return types[type] || types["default"];
};

export const CONFIG = {
	ITEMS_PER_PAGE: 10,
	ADDRESS_PREVIEW_LENGTH: 4,
	COPY_TIMEOUT: 2000,
};

// Reducer initial state
export const initialState: WalletState = {
	isLoading: false,
	isNFTLoading: false,
	isRefreshing: false,
	error: null,
	balance: 0,
	nfts: [],
	compressedNfts: [],
	activeTab: "tokens",
	currentPage: 1,
	compressedCurrentPage: 1,
	copied: false,
	showDisconnectConfirm: false,
}; // Reducer actions

export enum ACTION_TYPES {
	SET_LOADING = "SET_LOADING",
	SET_NFT_LOADING = "SET_NFT_LOADING",
	SET_REFRESHING = "SET_REFRESHING",
	SET_ERROR = "SET_ERROR",
	CLEAR_ERROR = "CLEAR_ERROR",
	SET_BALANCE = "SET_BALANCE",
	SET_NFTS = "SET_NFTS",
	SET_COMPRESSED_NFTS = "SET_COMPRESSED_NFTS",
	SET_ACTIVE_TAB = "SET_ACTIVE_TAB",
	SET_CURRENT_PAGE = "SET_CURRENT_PAGE",
	SET_COMPRESSED_CURRENT_PAGE = "SET_COMPRESSED_CURRENT_PAGE",
	SET_COPIED = "SET_COPIED",
	TOGGLE_DISCONNECT_CONFIRM = "TOGGLE_DISCONNECT_CONFIRM",
	RESET_STATE = "RESET_STATE",
}
export interface WalletState {
	isLoading: boolean;
	isNFTLoading: boolean;
	isRefreshing: boolean;
	error: string | null;
	balance: number;
	nfts: NFT[];
	compressedNfts: NFT[];
	activeTab: string;
	currentPage: number;
	compressedCurrentPage: number;
	copied: boolean;
	showDisconnectConfirm: boolean;
}

// Reducer function
export const walletReducer = (
	state: WalletState,
	action: WalletAction
): WalletState => {
	switch (action.type) {
		case ACTION_TYPES.SET_LOADING:
			return { ...state, isLoading: action.payload };
		case ACTION_TYPES.SET_NFT_LOADING:
			return { ...state, isNFTLoading: action.payload };
		case ACTION_TYPES.SET_REFRESHING:
			return { ...state, isRefreshing: action.payload };
		case ACTION_TYPES.SET_ERROR:
			return { ...state, error: action.payload };
		case ACTION_TYPES.CLEAR_ERROR:
			return { ...state, error: null };
		case ACTION_TYPES.SET_BALANCE:
			return { ...state, balance: action.payload };
		case ACTION_TYPES.SET_NFTS:
			return { ...state, nfts: action.payload };
		case ACTION_TYPES.SET_COMPRESSED_NFTS:
			return { ...state, compressedNfts: action.payload };
		case ACTION_TYPES.SET_ACTIVE_TAB:
			return { ...state, activeTab: action.payload };
		case ACTION_TYPES.SET_CURRENT_PAGE:
			return { ...state, currentPage: action.payload };
		case ACTION_TYPES.SET_COMPRESSED_CURRENT_PAGE:
			return { ...state, compressedCurrentPage: action.payload };
		case ACTION_TYPES.SET_COPIED:
			return { ...state, copied: action.payload };
		case ACTION_TYPES.TOGGLE_DISCONNECT_CONFIRM:
			return { ...state, showDisconnectConfirm: !state.showDisconnectConfirm };
		case ACTION_TYPES.RESET_STATE:
			return initialState;
		default:
			return state;
	}
};

export const WORKFLOW_STEPS = {
	INIT: "init",
	DROP_PIN: "dropPin",
	DRAW_GEOFENCE: "drawGeofence",
	COMPLETE: "complete",
};

export const BATCH_SIZE = 3;
export const CONCURRENT_LIMIT = 2;
export const BATCH_DELAY_MS = 2000;
export const MAX_RETRIES = 5;

// Map-related constants
export const SNAGGABLE_CHECK_THROTTLE = 1000;
export const GEOLOCATION_CONTROL_RETRY_DELAY = 500;
export const GEOLOCATION_CONTROL_MAX_RETRIES = 10;

// Mapbox layer IDs
export const SOURCE_ID = "markers";
export const UNCLUSTERED_LAYER_ID = "unclustered-point";
export const CLUSTERS_LAYER_ID = "clusters";
export const GEOFENCE_SOURCE_ID = "geofence-source";
export const GEOFENCE_FILL_LAYER_ID = "geofence-fill";
export const GEOFENCE_LINE_LAYER_ID = "geofence-line";

// Cache-related constants
export const MEMORY_CACHE_TTL = 30000;
export const MAX_MEMORY_CACHE_SIZE = 50 * 1024 * 1024;
